import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Database, HardDrive, Layers, FileText, AlertCircle, CheckCircle2, RotateCw, TrendingUp } from 'lucide-react';

/**
 * Mongo Sağlık Raporu — Şub 03, 2026
 * --------------------------------------------------------------
 * Koleksiyon bazlı boyut, doc count, indeks bilgileri + S3 migration flag'leri.
 */
const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtCount = (n) => (n || 0).toLocaleString('tr-TR');

export default function MongoHealthSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/storage/mongo-health');
      setData(r.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Veri yükleniyor…
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-rose-500 py-12">Veri alınamadı.</div>;
  }

  const flags = data.storage_flags || {};
  const cols = data.collections || [];
  const top10 = cols.slice(0, 10);
  const maxSize = top10[0]?.storage_size || 1;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="mongo-health-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-indigo-500" /> Mongo Sağlık Raporu
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Koleksiyon başına boyut, doc count ve S3 taşıma durumu özeti. Veriler gerçek zamanlı `collStats` çağrısından gelir.
          </p>
        </div>
        <Button variant="outline" onClick={reload} disabled={loading} data-testid="mongo-health-refresh">
          <RotateCw className="h-4 w-4 mr-1" /> Yenile
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
              <HardDrive className="h-3 w-3" /> Toplam Depolama
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{fmtBytes(data.total_storage_bytes)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
              <FileText className="h-3 w-3" /> Toplam Doküman
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{fmtCount(data.total_docs)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
              <Layers className="h-3 w-3" /> Toplam İndeks
            </div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{fmtBytes(data.total_index_bytes)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Koleksiyon Sayısı</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{data.collection_count}</div>
          </CardContent>
        </Card>
      </div>

      {/* Storage migration flags */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" /> S3 Taşıma Durumu (documents koleksiyonu)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-rose-50 dark:bg-rose-950/30 rounded p-3 border border-rose-200/50">
              <div className="text-[10px] uppercase font-semibold text-rose-600">Mongo Binary'de</div>
              <div className="text-xl font-bold text-rose-700 tabular-nums">{fmtCount(flags.documents_with_file_content)}</div>
              <div className="text-[10px] text-slate-500">file_content alanı dolu</div>
            </div>
            <div className="bg-sky-50 dark:bg-sky-950/30 rounded p-3 border border-sky-200/50">
              <div className="text-[10px] uppercase font-semibold text-sky-600">S3'e Taşınmış</div>
              <div className="text-xl font-bold text-sky-700 tabular-nums">{fmtCount(flags.documents_on_s3)}</div>
              <div className="text-[10px] text-slate-500">storage_backend=s3</div>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded p-3 border border-emerald-200/50">
              <div className="text-[10px] uppercase font-semibold text-emerald-600">Mongo'dan Temizlendi</div>
              <div className="text-xl font-bold text-emerald-700 tabular-nums">{fmtCount(flags.documents_cleaned)}</div>
              <div className="text-[10px] text-slate-500">file_content_cleaned_at dolu</div>
            </div>
          </div>
          {flags.documents_with_file_content === 0 && flags.documents_cleaned > 0 && (
            <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Tüm dökümanlar başarıyla S3'e taşındı, Mongo binary temizliği tamam.
            </div>
          )}
          {flags.documents_with_file_content > 0 && (
            <div className="mt-3 text-xs text-amber-700 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Hâlâ {fmtCount(flags.documents_with_file_content)} kayıt Mongo'da binary tutuyor. Ayarlar &rarr; Depolama'dan migration/cleanup çalıştırın.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top collections by size */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">En Büyük 10 Koleksiyon (boyuta göre)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {top10.map((c) => (
            <div key={c.name} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-mono font-medium">{c.name}</span>
                <span className="text-slate-500 tabular-nums">
                  <strong className="text-slate-700 dark:text-slate-200">{fmtBytes(c.storage_size)}</strong>
                  {' · '}{fmtCount(c.count)} doc{c.count !== 1 ? "'s" : ''}
                  {' · '}{c.nindexes} idx
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded">
                <div
                  className="h-full rounded bg-gradient-to-r from-indigo-400 to-indigo-600"
                  style={{ width: `${Math.max(1, (c.storage_size / maxSize) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Full table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Tüm Koleksiyonlar ({cols.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2">Koleksiyon</th>
                  <th className="text-right py-2">Doc Sayısı</th>
                  <th className="text-right py-2">Ortalama Boyut</th>
                  <th className="text-right py-2">Storage</th>
                  <th className="text-right py-2">İndeks</th>
                  <th className="text-right py-2">İndeks #</th>
                </tr>
              </thead>
              <tbody>
                {cols.map((c) => (
                  <tr key={c.name} className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-1.5 font-mono">{c.name}</td>
                    <td className="py-1.5 text-right tabular-nums">{fmtCount(c.count)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{fmtBytes(c.avg_obj_size)}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium">{fmtBytes(c.storage_size)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{fmtBytes(c.index_size)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{c.nindexes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
